import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLElement} cartDiscountErrorDuplicate - The duplicate-code error element.
 * @property {HTMLElement} cartDiscountErrorGeneric - The generic network/server error element.
 * @property {HTMLElement} cartDiscountShippingApplied - The "shipping discount applied" note element.
 * @property {HTMLElement} cartDiscountPersonalKept - The "personal code kept for checkout" note element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  // cartDiscountErrorDuplicate / cartDiscountErrorGeneric — нові варіанти,
  // не requiredRefs, щоб не ламати інші магазини, які не бачили оновленого
  // snippet (фолбек на discount_code, якщо їх немає).
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;

    const abortController = this.#createAbortController();

    try {
      this.#hideAllErrors();

      // Джерело правди — коди самого кошика (/cart.js), а не пілюлі в DOM:
      // знижки на ДОСТАВКУ не створюють discount application у кошику (вони
      // рахуються лише на чекауті), тож Liquid їх не рендерить і по пілюлях
      // їх не видно — оновлення за пілюлями мовчки викидало б такий код.
      const existingDiscounts = await this.#cartDiscountCodes(abortController.signal);
      if (existingDiscounts.some((code) => code.toUpperCase() === discountCodeValue.toUpperCase())) {
        // Раніше тут був мовчазний return — користувач бачив тільки червону
        // крапку без пояснення. Тепер показуємо явний банер.
        this.#handleDiscountError('duplicate', discountCodeValue);
        return;
      }

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        // Кошик не знає покупця, поки той не увійшов — персональні коди
        // (прив'язані до акаунта) для гостя завжди «непридатні», хоч і активні.
        // Не викидаємо такий код: чекаут перевірить його ще раз, знаючи email.
        if (this.dataset.loggedIn !== '1' && this.refs.cartDiscountPersonalKept) {
          this.#reconcileHiddenCodes([{ code: discountCodeValue, applicable: true }]);
          this.refs.cartDiscountPersonalKept.classList.remove('hidden');
          discountCode.value = '';
          return;
        }
        // Невалідний код «прилипає» до кошика як non-applicable — одразу
        // прибираємо, інакше він накопичується і їде на чекаут сміттям.
        const cleanup = fetchConfig('json', {
          body: JSON.stringify({ discount: existingDiscounts.join(',') }),
        });
        fetch(Theme.routes.cart_update_url, cleanup).catch(() => {});
        discountCode.value = '';
        this.#handleDiscountError('discount_code', discountCodeValue);
        return;
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);

      // Shipping-коди застосовані, але невидимі для Liquid — домальовуємо їм
      // пілюлі; якщо серед них щойно введений код, показуємо зелену підказку
      // «нарахується на чекауті» замість колишньої червоної помилки.
      const appliedHiddenNewCode = this.#reconcileHiddenCodes(data.discount_codes, discountCodeValue);
      if (appliedHiddenNewCode) this.#showShippingApplied();
      discountCode.value = '';
    } catch (error) {
      // Раніше catch був порожній — мережеві помилки зникали без сліду.
      if (error && error.name !== 'AbortError') {
        console.error('[cart-discount] apply failed:', error);
        this.#handleDiscountError('generic');
      }
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const abortController = this.#createAbortController();

    try {
      this.#hideAllErrors();

      // Коди беремо з кошика (разом із невидимими shipping-кодами), інакше
      // видалення однієї пілюлі мовчки викидало б і знижку на доставку.
      const existingDiscounts = (await this.#cartDiscountCodes(abortController.signal)).filter(
        (code) => code.toUpperCase() !== discountCode.toUpperCase(),
      );

      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
      this.#reconcileHiddenCodes(data.discount_codes);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Коди знижок, реально застосовані до кошика — з /cart.js, включно з
   * shipping-кодами, яких немає в discount applications Liquid.
   * Фолбек на пілюлі DOM, якщо запит не вдався.
   * @param {AbortSignal} [signal]
   * @returns {Promise<string[]>}
   */
  async #cartDiscountCodes(signal) {
    try {
      const cartUrl = (Theme.routes && Theme.routes.cart_url) || '/cart';
      const response = await fetch(`${cartUrl}.js`, { signal });
      const cart = await response.json();
      return (cart.discount_codes || [])
        .map((/** @type {{ code?: string }} */ discount) => discount.code)
        .filter(Boolean);
    } catch (error) {
      if (error && error.name === 'AbortError') throw error;
      return this.#existingDiscounts();
    }
  }

  /**
   * Домальовує пілюлі для applicable-кодів, які Liquid не відрендерив
   * (знижки на доставку). Повертає true, якщо серед них був щойно введений код.
   * @param {{ code: string; applicable: boolean }[]} discountCodes
   * @param {string} [newCode]
   * @returns {boolean}
   */
  #reconcileHiddenCodes(discountCodes, newCode) {
    const list = this.querySelector('.cart-discount__codes');
    if (!list || !Array.isArray(discountCodes)) return false;

    const visible = new Set(this.#existingDiscounts().map((code) => code.toUpperCase()));
    let appliedNewCode = false;
    let injected = false;
    for (const discount of discountCodes) {
      if (!discount.applicable || !discount.code) continue;
      if (visible.has(discount.code.toUpperCase())) continue;
      if (newCode && discount.code.toUpperCase() === newCode.toUpperCase()) appliedNewCode = true;

      const pill = document.createElement('li');
      pill.className = 'cart-discount__pill';
      pill.dataset.discountCode = discount.code;

      const label = document.createElement('p');
      label.className = 'cart-discount__pill-code';
      label.textContent = discount.code;

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'cart-discount__pill-remove svg-wrapper svg-wrapper--smaller button-unstyled';
      remove.setAttribute('aria-label', `Remove discount ${discount.code}`);
      remove.textContent = '✕';
      remove.addEventListener('click', this.removeDiscount);

      pill.append(label, remove);
      list.append(pill);
      injected = true;
    }
    if (injected) {
      this.querySelector('.discount-details')?.setAttribute('open', '');
    }
    return appliedNewCode;
  }

  /** Зелена підказка «знижка на доставку нарахується на чекауті». */
  #showShippingApplied() {
    this.refs.cartDiscountShippingApplied?.classList.remove('hidden');
  }

  /**
   * Після перезавантаження сторінки shipping-коди теж мають бути видні —
   * Liquid їх не рендерить, тож домальовуємо з даних кошика.
   */
  connectedCallback() {
    super.connectedCallback?.();
    this.#cartDiscountCodes()
      .then((codes) => {
        if (codes.length === 0) return;
        this.#reconcileHiddenCodes(codes.map((code) => ({ code, applicable: true })));
      })
      .catch(() => {});
  }

  /**
   * Hides all error variants. Викликаємо перед кожною спробою застосування,
   * щоб старі повідомлення не залишались.
   */
  #hideAllErrors() {
    const refs = this.refs;
    refs.cartDiscountError?.classList.add('hidden');
    refs.cartDiscountErrorDiscountCode?.classList.add('hidden');
    refs.cartDiscountErrorShipping?.classList.add('hidden');
    refs.cartDiscountErrorDuplicate?.classList.add('hidden');
    refs.cartDiscountErrorGeneric?.classList.add('hidden');
    refs.cartDiscountShippingApplied?.classList.add('hidden');
    refs.cartDiscountPersonalKept?.classList.add('hidden');
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping' | 'duplicate' | 'generic'} type - The type of discount error.
   * @param {string} [code] - Optional code value for templated messages.
   */
  #handleDiscountError(type, code) {
    const refs = this.refs;
    const map = {
      discount_code: refs.cartDiscountErrorDiscountCode,
      shipping: refs.cartDiscountErrorShipping,
      duplicate: refs.cartDiscountErrorDuplicate,
      generic: refs.cartDiscountErrorGeneric,
    };
    // Фолбек: якщо нового елемента немає у DOM (старий snippet) —
    // показуємо стандартну "discount_code" помилку.
    const target = map[type] || refs.cartDiscountErrorDiscountCode;
    refs.cartDiscountError?.classList.remove('hidden');
    target?.classList.remove('hidden');
    // Підставляємо {code} у data-template, якщо є
    if (target && code) {
      const tpl = target.dataset.template;
      if (tpl) target.textContent = tpl.replace(/\[code\]/g, code);
    }
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
